/**
 * Back to the page that asked for a sign-in (the user, 2026-10-13: "after the
 * login it always goes to My raves — it is not getting back to where it was").
 *
 * **Any MyRave page** — the rave admin, the console, a rave, the account —
 * writes its own full address into a short-lived cookie on the platform domain
 * (`myrave_return`, 30 minutes, like the sign-out marker) before the hop to
 * `login.`; signing in (a password at `login.`, or a provider landing on
 * `app./`) goes back there ONCE (`afterSignIn`, `ReturnAfterSignIn`). Still
 * no `?next=`: nothing in an address is a destination, and the cookie is only
 * followed to an address on one of the platform's own hosts — never `login.`,
 * never `api.`, never another site — so it is no open redirect.
 *
 * The older note below stays for the two app pages it was made for.
 *
 * Back to the page that asked for a sign-in — for a SHORT list of app pages.
 *
 * Signing in lands on `app./` and there is no `?next=` (`LoginPage.tsx`: it
 * sent people back to a homepage they had finished with, and it was an open
 * redirect surface). One exception, the user's (2026-10-08): somebody sent to
 * `app.…/plans` — from a rave's paywall, "See deals and plans" — signs in and
 * should be on the plans, not on the dashboard wondering where they went.
 *
 * So the app host remembers the path in its OWN storage before the hop to
 * `login.`, only when it is on the list below, and takes it back once on the
 * landing. Nothing a caller supplies is ever a destination: the path must be
 * one of these, on this host, and recent.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appUrl, isAppHost, isLoginHost, platformDomain } from '@/lib/tenant';
import { clearPlatformCookie, readPlatformCookie, writePlatformCookie } from '@/lib/signOut';
import { readJson, remove, writeJson } from '@/lib/storage';

// `/admin-invitation`: an invitation to run a rave (step 64.1) — its token
// waits in this host's storage (`AdminInvitation.tsx`), never in the address.
const RETURNABLE = new Set(['/plans', '/admin-invitation']);
const NAME = 'after-sign-in';
/** A sign-in takes minutes, not hours; an older note is a forgotten one. */
const FRESH_MS = 30 * 60_000;

const RETURN_COOKIE = 'myrave_return';

/** An address a sign-in may go back to: one of the platform's own hosts — never `login.`, `api.` or `media.`. */
export function isReturnable(href: string): boolean {
  let u: URL;
  try { u = new URL(href); } catch { return false; }
  const domain = platformDomain().toLowerCase();
  const host = u.hostname.toLowerCase();
  if (u.protocol !== 'https:' && !(u.protocol === 'http:' && /localhost$/.test(domain))) return false;
  if (host !== domain && !host.endsWith(`.${domain}`)) return false;
  const label = host === domain ? '' : host.slice(0, -(domain.length + 1));
  return !['login', 'api', 'media', 'media-staging'].includes(label);
}

/**
 * **The public site's own landing is not somewhere to come back to.**
 *
 * The bare platform domain's `/` sends a signed-in visitor straight on to
 * `app./` (`PublicSite.tsx`), so remembering it is remembering nothing — and
 * worse, it OVERWRITES a real return. The user, 2026-10-28: "the login doesn't
 * get you back to the page you were at", from `app./artists` → the public site
 * → `login.` → `app./`. Their `/artists` was replaced by the landing on the way
 * through. `app./` was already excluded for the same reason; this is its twin.
 */
function isPublicLanding(href: string): boolean {
  try {
    const u = new URL(href);
    return u.hostname.toLowerCase() === platformDomain().toLowerCase() && u.pathname === '/';
  } catch {
    return false;
  }
}

/** Before the hop to `login.`: remember this page to come back to. */
export function rememberReturn(pathname = window.location.pathname, href = window.location.href): void {
  // Any page of the platform, as its full address — except the sign-in page, the
  // app's bare landing and the public site's (neither is a destination).
  // `href` is this page unless the caller adds to it (a set to save, `lib/signInFirst.ts`).
  if (!isLoginHost() && !(isAppHost() && pathname === '/') && !isPublicLanding(href) && isReturnable(href)) {
    writePlatformCookie(RETURN_COOKIE, encodeURIComponent(href), FRESH_MS / 1000);
  } else if (isAppHost() && pathname === '/') {
    // Signing in from the app's own landing: an older page waiting here would win instead
    // (staging, 2026-10-30: a console visit half an hour before sent the sign-in to `appadm.`).
    forgetReturn();
  }
  if (!isAppHost() || !RETURNABLE.has(pathname)) return;
  writeJson(null, NAME, { path: pathname, at: Date.now() });
}

/** Nothing to come back to — signing out, or signing in from a page that is no destination. */
export function forgetReturn(): void {
  clearPlatformCookie(RETURN_COOKIE);
}

/** The page a sign-in comes back to — taken ONCE — or null. */
export function takeReturnUrl(): string | null {
  const raw = readPlatformCookie(RETURN_COOKIE);
  if (raw === null) return null;
  writePlatformCookie(RETURN_COOKIE, '', 0);
  let href: string;
  try { href = decodeURIComponent(raw); } catch { return null; }
  return href && isReturnable(href) ? href : null;
}

/**
 * The page waiting to be gone back to, without forgetting it — the account's
 * `/complete` page reads it: the Rideboard's door sends somebody there for a
 * date of birth or a phone and they come back (2026-10-21).
 */
export function peekReturnUrl(): string | null {
  const raw = readPlatformCookie(RETURN_COOKIE);
  if (!raw) return null;
  let href: string;
  try { href = decodeURIComponent(raw); } catch { return null; }
  return href && isReturnable(href) ? href : null;
}

/** Where a finished sign-in goes: back to the page that sent the person here, else the app. */
export function afterSignIn(): string {
  return takeReturnUrl() ?? appUrl('/');
}

/** The page a sign-in will come back to, without forgetting it. */
export function peekReturn(now = Date.now()): string | null {
  const note = readJson<{ path?: unknown; at?: unknown } | null>(null, NAME, null);
  if (!note || typeof note.path !== 'string' || !RETURNABLE.has(note.path)) return null;
  if (typeof note.at !== 'number' || now - note.at > FRESH_MS) return null;
  return note.path;
}

/** The page to come back to, once — then forgotten. */
export function takeReturn(now = Date.now()): string | null {
  const note = readJson<{ path?: unknown; at?: unknown } | null>(null, NAME, null);
  if (!note) return null;
  remove(null, NAME);
  if (typeof note.path !== 'string' || !RETURNABLE.has(note.path)) return null;
  if (typeof note.at !== 'number' || now - note.at > FRESH_MS) return null;
  return note.path;
}

/**
 * On the app host's landing, inside the session gate: go back if there is somewhere to go.
 *
 * **The landing is read when this first RENDERS, not in its effect** (2026-10-20).
 * A new account attends nothing, and My raves sends it on to Find a rave (the
 * user: a new account lands "straight to the Find a rave page"); an effect
 * that ran after that bounce saw `/raves`, decided nobody had landed, and the
 * page the sign-in was asked from (`/plans`) was lost. Rendering comes before
 * any effect, so the address here is the one the person arrived on.
 */
export function ReturnAfterSignIn() {
  const navigate = useNavigate();
  const [landed] = useState(() => window.location.pathname === '/');
  useEffect(() => {
    if (!landed) return;
    // A provider's sign-in lands here: back to the page that asked, on whichever host it was.
    const back = takeReturnUrl();
    if (back) {
      const u = new URL(back);
      if (u.origin === window.location.origin) {
        if (u.pathname !== '/' || u.search || u.hash) navigate(`${u.pathname}${u.search}${u.hash}`, { replace: true });
      } else window.location.replace(back);
      return;
    }
    const path = takeReturn();
    if (path) navigate(path, { replace: true });
  }, [navigate, landed]);
  return null;
}
